import React, { useState, useEffect } from 'react';
import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const REQUIRED_CONTRACTOR_AGREEMENTS = [
  'not_employer', 'supervising', 'liability', 'no_tradepower_liability',
];

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-skilltrade-app-2026.cloudfunctions.net';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const getResponseTime = (avgMinutes) => {
  if (avgMinutes === undefined || avgMinutes === null) return null;
  if (avgMinutes <= 60) return '⚡ Responds within 1 hour';
  const hours = Math.round(avgMinutes / 60);
  if (hours <= 12) return `⚡ Responds within ${hours} hour${hours > 1 ? 's' : ''}`;
  const days = Math.round(avgMinutes / (60 * 24));
  return `🕐 Responds within ${days} day${days > 1 ? 's' : ''}`;
};

function CardSetupForm({ contractorId, contractorEmail, companyName, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/createContractorSetup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId, contractorEmail, companyName }),
      });
      const data = await res.json();
      if (!data.clientSecret) throw new Error('Failed to create setup intent');

      const result = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: contractorEmail },
        },
      });

      if (result.error) {
        setError(result.error.message);
        setSaving(false);
        return;
      }

      const confirmRes = await fetch(`${CLOUD_FUNCTIONS_BASE}/confirmContractorPaymentMethod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId }),
      });
      const confirmData = await confirmRes.json();

      if (confirmData.setupDone) {
        onSuccess(confirmData);
      } else {
        setError('Card saved but verification failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={cardFormStyles.cardWrapper}>
        <CardElement options={{
          style: {
            base: { fontSize: '16px', color: '#1a1a2e', '::placeholder': { color: '#aaa' } },
            invalid: { color: '#dc3545' },
          },
        }} />
      </div>
      {error && <p style={cardFormStyles.errorText}>⚠️ {error}</p>}
      <div style={cardFormStyles.buttons}>
        <button type="button" style={cardFormStyles.cancelBtn} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          style={saving ? cardFormStyles.saveBtnDisabled : cardFormStyles.saveBtn}
          disabled={saving || !stripe}>
          {saving ? '⏳ Saving...' : '💳 Save Card & Send Request'}
        </button>
      </div>
    </form>
  );
}

const cardFormStyles = {
  cardWrapper: { border: '1px solid #ddd', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', backgroundColor: 'white' },
  errorText: { fontSize: '13px', color: '#dc3545', marginBottom: '12px' },
  buttons: { display: 'flex', gap: '10px' },
  cancelBtn: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveBtn: { flex: 2, padding: '12px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  saveBtnDisabled: { flex: 2, padding: '12px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
};

function WorkerDetail() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [contractorAgreementsComplete, setContractorAgreementsComplete] = useState(true);
  const [contractorPaymentSetup, setContractorPaymentSetup] = useState(false);
  const [contractorCardInfo, setContractorCardInfo] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showPaymentSetup, setShowPaymentSetup] = useState(false);
  const [jobDate, setJobDate] = useState('');
  const [jobEndDate, setJobEndDate] = useState('');
  const [jobDays, setJobDays] = useState(1);
  const [includeSaturdays, setIncludeSaturdays] = useState(false);
  const [includeSundays, setIncludeSundays] = useState(false);
  const [datesLocked, setDatesLocked] = useState(false);
  const [jobTime, setJobTime] = useState('');
  const [jobDuration, setJobDuration] = useState('4');
  const [jobAddress, setJobAddress] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [addBackgroundCheck, setAddBackgroundCheck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const BACKGROUND_CHECK_FEE = 39.99;

  // Fix for mobile — wait for auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const start = params.get('start');
    const end = params.get('end');
    const sat = params.get('sat') === 'true';
    const sun = params.get('sun') === 'true';
    if (start && end) {
      setJobDate(start);
      setJobEndDate(end);
      setIncludeSaturdays(sat);
      setIncludeSundays(sun);
      setDatesLocked(true);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const workerSnap = await getDoc(doc(db, 'workerProfiles', workerId));
        if (workerSnap.exists()) setWorker({ id: workerSnap.id, ...workerSnap.data() });

        if (user) {
          const contractorSnap = await getDoc(doc(db, 'contractorProfiles', user.uid));
          if (contractorSnap.exists()) {
            const contractorData = contractorSnap.data();
            const allComplete = REQUIRED_CONTRACTOR_AGREEMENTS.every(
              id => contractorData.agreements?.[id] === true
            );
            setContractorAgreementsComplete(allComplete);
            if (contractorData.stripeSetupDone) {
              setContractorPaymentSetup(true);
              setContractorCardInfo({
                last4: contractorData.stripeCardLast4,
                brand: contractorData.stripeCardBrand,
              });
            }
          } else {
            setContractorAgreementsComplete(false);
          }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [workerId, user]);

  useEffect(() => {
    if (window.google) { setMapsLoaded(true); return; }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) { existing.addEventListener('load', () => setMapsLoaded(true)); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (jobDate && jobEndDate) {
      const start = new Date(jobDate + 'T00:00:00');
      const end = new Date(jobEndDate + 'T00:00:00');
      let count = 0;
      const current = new Date(start);
      while (current <= end) {
        const day = current.getDay();
        if (day >= 1 && day <= 5) count++;
        if (day === 6 && includeSaturdays) count++;
        if (day === 0 && includeSundays) count++;
        current.setDate(current.getDate() + 1);
      }
      setJobDays(count > 0 ? count : 1);
    }
  }, [jobDate, jobEndDate, includeSaturdays, includeSundays]);

  const handleAddressInput = async (e) => {
    const val = e.target.value;
    setJobAddress(val);
    if (val.length > 2 && window.google && mapsLoaded) {
      try {
        const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
        const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: val, includedRegionCodes: ['us'],
        });
        setSuggestions(result.suggestions.map(s => ({
          place_id: s.placePrediction.placeId,
          description: s.placePrediction.text.text,
        })));
        setShowSuggestions(true);
      } catch (err) { setSuggestions([]); }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const calculateTotal = () => {
    const laborTotal = Math.ceil(worker.hourlyRate * 1.15 * parseInt(jobDuration) * jobDays);
    return Math.ceil(laborTotal + (addBackgroundCheck ? BACKGROUND_CHECK_FEE : 0));
  };

  const handleCardSuccess = (cardInfo) => {
    setContractorPaymentSetup(true);
    setContractorCardInfo(cardInfo);
    setShowPaymentSetup(false);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!contractorAgreementsComplete) {
      alert('Please complete your platform agreements first.');
      navigate('/contractor-profile');
      return;
    }
    if (!contractorPaymentSetup) {
      setShowPaymentSetup(true);
      return;
    }
    if (!jobDate || !jobEndDate) { alert('Please select job dates.'); return; }
    if (!jobTime) { alert('Please select a start time.'); return; }
    if (!jobAddress.trim()) { alert('Please enter a job site address.'); return; }
    if (!jobDescription.trim()) { alert('Please describe the work needed.'); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'jobRequests'), {
        workerId: worker.id,
        workerName: worker.name,
        workerEmail: worker.email,
        contractorId: user.uid,
        contractorEmail: user.email,
        jobDate, jobEndDate, jobDays,
        includeSaturdays, includeSundays,
        jobTime, jobDuration: parseInt(jobDuration),
        jobAddress, jobDescription,
        workerRate: worker.hourlyRate,
        contractorRate: Math.ceil(worker.hourlyRate * 1.15),
        addBackgroundCheck,
        backgroundCheckFee: addBackgroundCheck ? BACKGROUND_CHECK_FEE : 0,
        totalEstimate: calculateTotal(),
        status: 'pending',
        createdAt: new Date(),
      });
      setSubmitted(true);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  if (loading) return <div style={styles.loadingContainer}><p>Loading worker profile...</p></div>;
  if (!worker) return (
    <div style={styles.loadingContainer}>
      <p>Worker not found.</p>
      <button onClick={() => navigate('/browse')} style={styles.backBtn}>Back to Browse</button>
    </div>
  );

  const contractorRate = Math.ceil(worker.hourlyRate * 1.15);
  const tradeProfiles = worker.tradeProfiles || [];

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
      </div>

      <div style={styles.content}>
        {/* Worker profile card */}
        <div style={styles.profileCard}>
          <div style={styles.profileHeader}>
            {worker.photoData ? (
              <img src={worker.photoData} alt={worker.name} style={styles.avatar} />
            ) : (
              <div style={styles.avatarPlaceholder}><span style={styles.avatarIcon}>👤</span></div>
            )}
            <div style={styles.profileInfo}>
              <h2 style={styles.workerName}>{worker.name}</h2>
              <div style={styles.ratingRow}>
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star} style={{ color: star <= Math.round(worker.rating || 0) ? '#FFB800' : '#ddd', fontSize: '18px' }}>★</span>
                ))}
                <span style={styles.ratingText}>
                  {worker.rating > 0 ? `${worker.rating.toFixed(1)} (${worker.reviewCount} reviews)` : 'No reviews yet'}
                </span>
              </div>
              {getResponseTime(worker.avgResponseMinutes) && (
                <div style={styles.responseTimeBadge}>{getResponseTime(worker.avgResponseMinutes)}</div>
              )}
              <div style={{ marginTop: '6px' }}>
                {(worker.noShows > 0 || worker.lateCancellations > 0) ? (
                  <span style={styles.reliabilityBadgeWarn}>
                    ⚠️ {[
                      worker.lateCancellations > 0 && `${worker.lateCancellations} late cancel${worker.lateCancellations > 1 ? 's' : ''}`,
                      worker.noShows > 0 && `${worker.noShows} no-show${worker.noShows > 1 ? 's' : ''}`
                    ].filter(Boolean).join(' • ')}
                  </span>
                ) : (
                  <span style={styles.reliabilityBadgeGood}>✅ Reliable</span>
                )}
              </div>
              <p style={styles.location}>📍 {worker.zipCode} • Within {worker.radius} miles</p>
            </div>
            <div style={styles.rateBox}>
              <span style={styles.rate}>${contractorRate}</span>
              <span style={styles.perHour}>/hr</span>
            </div>
          </div>

          {worker.bio && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>About</h3>
              <p style={styles.bio}>{worker.bio}</p>
            </div>
          )}

          {tradeProfiles.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Trade Profiles</h3>
              <div style={styles.tradeProfilesList}>
                {tradeProfiles.map(tp => (
                  <div key={tp.id} style={styles.tradeProfileCard}>
                    <div style={styles.tradeProfileHeader}>
                      <span style={styles.tradeProfileTrade}>{tp.trade}</span>
                      <div style={styles.tradeProfileBadgeRow}>
                        <span style={styles.levelBadge}>{tp.level}</span>
                        <span style={styles.yearsBadge}>{tp.yearsExperience} yrs exp</span>
                        {tp.licenseStatus === 'verified' && <span style={styles.verifiedLicenseBadge}>🏛️ Licensed Contractor</span>}
                        {tp.licenseStatus === 'pending' && <span style={styles.pendingLicenseBadge}>⏳ License Pending</span>}
                        {(!tp.requiresLicense || tp.level !== 'Master') && <span style={styles.noLicenseBadge}>✅ No License Required at This Level</span>}
                      </div>
                    </div>
                    {tp.industries?.length > 0 && (
                      <div style={styles.tradeProfileDetailRow}>
                        <span style={styles.tradeDetailLabel}>Industries:</span>
                        <div style={styles.tagRow}>{tp.industries.map(i => <span key={i} style={styles.industryTag}>{i}</span>)}</div>
                      </div>
                    )}
                    {tp.specializations?.length > 0 && (
                      <div style={styles.tradeProfileDetailRow}>
                        <span style={styles.tradeDetailLabel}>Specializations:</span>
                        <div style={styles.tagRow}>{tp.specializations.map(s => <span key={s} style={styles.specTag}>{s}</span>)}</div>
                      </div>
                    )}
                    {tp.licenseNumber && (
                      <div style={styles.tradeProfileDetailRow}>
                        <span style={styles.tradeDetailLabel}>License:</span>
                        <span style={styles.licenseText}>{tp.licenseState} {tp.licenseType} #{tp.licenseNumber}{tp.licenseExpiry && ` — Exp: ${tp.licenseExpiry}`}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tradeProfiles.length === 0 && worker.trades?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Trades</h3>
              <div style={styles.tagsRow}>
                {worker.trades.map(trade => <span key={trade} style={styles.tradeTag}>{trade}</span>)}
              </div>
            </div>
          )}

          {worker.certifications?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Certifications</h3>
              <div style={styles.tagsRow}>
                {worker.certifications.map(cert => <span key={cert} style={styles.certBadge}>{cert}</span>)}
              </div>
            </div>
          )}

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Compliance</h3>
            <div style={styles.complianceRow}>
              {worker.workAuthorized && <span style={styles.complianceBadge}>✓ Work Authorized</span>}
              {worker.w9Consent && <span style={styles.complianceBadge}>✓ 1099 Acknowledged</span>}
              {worker.insuranceVerified && <span style={styles.complianceBadge}>🛡️ Carries Own Insurance</span>}
            </div>
          </div>
        </div>

        {!contractorAgreementsComplete && (
          <div style={styles.agreementsIncompleteCard}>
            <h3 style={styles.agreementsIncompleteTitle}>⚠️ Complete Your Platform Agreements First</h3>
            <p style={styles.agreementsIncompleteText}>
              You must acknowledge all required platform agreements in your contractor profile before you can send job requests.
            </p>
            <button style={styles.goToProfileBtn} onClick={() => navigate('/contractor-profile')}>
              Complete Agreements in My Profile →
            </button>
          </div>
        )}

        {contractorAgreementsComplete && !contractorPaymentSetup && !showPaymentSetup && !submitted && (
          <div style={styles.paymentSetupCard}>
            <div style={styles.paymentSetupIcon}>💳</div>
            <h3 style={styles.paymentSetupTitle}>Add a Payment Method to Send Requests</h3>
            <p style={styles.paymentSetupText}>
              You need a payment method on file before sending job requests.
              Workers need to know payment is guaranteed before accepting work.
              No charge until hours are confirmed.
            </p>
            <div style={styles.paymentSetupFeatures}>
              <div style={styles.paymentSetupFeature}><span>💰</span><span>Only charged for confirmed hours — never estimates</span></div>
              <div style={styles.paymentSetupFeature}><span>📅</span><span>Billed weekly every Thursday for the prior week</span></div>
              <div style={styles.paymentSetupFeature}><span>🔒</span><span>Secured by Stripe — PCI compliant</span></div>
            </div>
            <button style={styles.paymentSetupBtn} onClick={() => setShowPaymentSetup(true)}>Add Payment Method →</button>
          </div>
        )}

        {contractorAgreementsComplete && !contractorPaymentSetup && showPaymentSetup && !submitted && (
          <div style={styles.cardEntryCard}>
            <h3 style={styles.cardEntryTitle}>💳 Add Payment Method</h3>
            <p style={styles.cardEntrySubtitle}>
              Your card will be saved securely by Stripe. You will only be charged for confirmed hours, billed weekly every Thursday.
            </p>
            <div style={styles.cardEntryInfo}>
              <span style={styles.cardEntryInfoIcon}>ℹ️</span>
              <span style={styles.cardEntryInfoText}>No charge today. First charge will be on the Thursday after your first worker confirms hours.</span>
            </div>
            <Elements stripe={stripePromise}>
              <CardSetupForm
                contractorId={user?.uid}
                contractorEmail={user?.email}
                companyName=""
                onSuccess={handleCardSuccess}
                onCancel={() => setShowPaymentSetup(false)}
              />
            </Elements>
          </div>
        )}

        {contractorAgreementsComplete && contractorPaymentSetup && !submitted && (
          <div style={styles.paymentOnFileBanner}>
            ✅ Payment method on file
            {contractorCardInfo?.brand && contractorCardInfo?.last4 && (
              <span style={styles.cardInfoText}>
                {' '}— {contractorCardInfo.brand.charAt(0).toUpperCase() + contractorCardInfo.brand.slice(1)} ••••{contractorCardInfo.last4}
              </span>
            )}
          </div>
        )}

        {contractorAgreementsComplete && contractorPaymentSetup && !submitted && (
          <div style={styles.requestCard}>
            <h3 style={styles.requestTitle}>
              {showRequestForm ? 'Send Job Request' : `Request ${worker.name.split(' ')[0]}`}
            </h3>

            {!showRequestForm ? (
              <div style={styles.requestPreview}>
                <p style={styles.requestText}>
                  Send a job request to <strong>{worker.name}</strong> with your job details.
                  They'll receive a notification and can accept or decline.
                </p>
                <div style={styles.estimateRow}>
                  <span style={styles.estimateLabel}>Rate:</span>
                  <span style={styles.estimateValue}>${contractorRate}/hr</span>
                </div>
                {datesLocked && jobDate && (
                  <div style={styles.lockedDatesPreview}>
                    📅 <strong>{formatDate(jobDate)}</strong>
                    {jobEndDate !== jobDate && ` → ${formatDate(jobEndDate)}`}
                    <span style={styles.lockedBadge}>🔒 Dates from your search</span>
                  </div>
                )}
                <button style={styles.requestButton} onClick={() => setShowRequestForm(true)}>
                  Send Job Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmitRequest}>
                {datesLocked ? (
                  <div style={styles.lockedDatesBox}>
                    <div style={styles.lockedDatesHeader}>
                      <span style={styles.lockedDatesTitle}>📅 Job Dates</span>
                      <span style={styles.lockedBadge}>🔒 Locked</span>
                    </div>
                    <div style={styles.lockedDatesContent}>
                      <div style={styles.lockedDateItem}>
                        <span style={styles.lockedDateLabel}>Start</span>
                        <span style={styles.lockedDateValue}>{formatDate(jobDate)}</span>
                      </div>
                      <div style={styles.lockedDateItem}>
                        <span style={styles.lockedDateLabel}>End</span>
                        <span style={styles.lockedDateValue}>{formatDate(jobEndDate)}</span>
                      </div>
                      <div style={styles.lockedDateItem}>
                        <span style={styles.lockedDateLabel}>Working Days</span>
                        <span style={styles.lockedDateValue}>
                          {jobDays} day{jobDays !== 1 ? 's' : ''}
                          {includeSaturdays || includeSundays ? ` (incl. ${[includeSaturdays && 'Sat', includeSundays && 'Sun'].filter(Boolean).join(' & ')})` : ' (weekdays only)'}
                        </span>
                      </div>
                    </div>
                    <p style={styles.lockedNote}>Dates are locked from your availability search to prevent double booking.</p>
                  </div>
                ) : (
                  <>
                    <div style={styles.row}>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>Start Date</label>
                        <input style={styles.input} type="date" value={jobDate}
                          onChange={e => {
                            setJobDate(e.target.value);
                            if (!jobEndDate || jobEndDate < e.target.value) setJobEndDate(e.target.value);
                          }}
                          min={new Date().toISOString().split('T')[0]} required />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>End Date</label>
                        <input style={styles.input} type="date" value={jobEndDate}
                          onChange={e => setJobEndDate(e.target.value)}
                          min={jobDate || new Date().toISOString().split('T')[0]} required />
                      </div>
                    </div>
                    <div style={styles.weekendBox}>
                      <span style={styles.weekendTitle}>📅 Weekend Work Days</span>
                      <div style={styles.weekendOptions}>
                        <label style={styles.weekendLabel}>
                          <input type="checkbox" checked={includeSaturdays} onChange={e => setIncludeSaturdays(e.target.checked)} style={styles.checkbox} />
                          Include Saturdays
                        </label>
                        <label style={styles.weekendLabel}>
                          <input type="checkbox" checked={includeSundays} onChange={e => setIncludeSundays(e.target.checked)} style={styles.checkbox} />
                          Include Sundays
                        </label>
                      </div>
                    </div>
                    {jobDate && jobEndDate && (
                      <p style={styles.daysNote}>📅 {jobDays} working day{jobDays !== 1 ? 's' : ''} selected</p>
                    )}
                  </>
                )}

                <label style={styles.fieldLabel}>Start Time</label>
                <input style={styles.input} type="time" value={jobTime} onChange={e => setJobTime(e.target.value)} required />

                <label style={styles.fieldLabel}>Hours Per Day (4 hour minimum)</label>
                <select style={styles.input} value={jobDuration} onChange={e => setJobDuration(e.target.value)}>
                  <option value="4">4 hours</option>
                  <option value="6">6 hours</option>
                  <option value="8">8 hours (full day)</option>
                  <option value="10">10 hours</option>
                  <option value="12">12 hours</option>
                </select>

                <label style={styles.fieldLabel}>Job Site Address</label>
                <div style={{ position: 'relative' }}>
                  <input style={styles.input} type="text" placeholder="Enter job site address"
                    value={jobAddress} onChange={handleAddressInput} autoComplete="off" required />
                  {showSuggestions && suggestions.length > 0 && (
                    <div style={styles.suggestionBox}>
                      {suggestions.map(s => (
                        <div key={s.place_id} style={styles.suggestionItem}
                          onMouseDown={() => { setJobAddress(s.description); setShowSuggestions(false); }}>
                          📍 {s.description}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <label style={styles.fieldLabel}>Job Description</label>
                <textarea style={styles.textarea} placeholder="Describe the work needed..."
                  value={jobDescription} onChange={e => setJobDescription(e.target.value)} rows={3} required />

                <div style={styles.addonBox}>
                  <div style={styles.addonHeader}>
                    <span style={styles.addonTitle}>🔍 Optional Add-ons</span>
                  </div>
                  <label style={styles.addonLabel}>
                    <input type="checkbox" checked={addBackgroundCheck}
                      onChange={e => setAddBackgroundCheck(e.target.checked)} style={styles.checkbox} />
                    <div style={styles.addonInfo}>
                      <span style={styles.addonName}>Background Check — $39.99</span>
                      <span style={styles.addonDesc}>Basic criminal verification. Recommended for public work, government contracts.</span>
                    </div>
                  </label>
                </div>

                <div style={styles.estimateBox}>
                  <span style={styles.estimateLabel}>Estimated Total</span>
                  <span style={styles.estimateTotal}>${calculateTotal()}</span>
                  <div style={styles.estimateBreakdown}>
                    <span style={styles.estimateNote}>${contractorRate}/hr x {jobDuration} hrs/day x {jobDays} day{jobDays > 1 ? 's' : ''}</span>
                    {addBackgroundCheck && <span style={styles.bgCheckNote}>+ $39.99 background check</span>}
                  </div>
                </div>

                <div style={styles.formButtons}>
                  <button type="button" style={styles.cancelButton} onClick={() => setShowRequestForm(false)}>Cancel</button>
                  <button type="submit" style={submitting ? styles.submitButtonDisabled : styles.submitButton} disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {submitted && (
          <div style={styles.successCard}>
            <p style={styles.successIcon}>🎉</p>
            <h3 style={styles.successTitle}>Request Sent!</h3>
            <p style={styles.successText}>
              Your job request has been sent to <strong>{worker.name}</strong>.
              They'll be notified and can accept or decline.
            </p>
            <button style={styles.requestButton} onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  navbar: { backgroundColor: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', touchAction: 'manipulation' },
  backBtn: { padding: '10px 20px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  content: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '24px' },
  profileCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px' },
  profileHeader: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  avatar: { width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #E94560', flexShrink: 0 },
  avatarPlaceholder: { width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  avatarIcon: { fontSize: '36px' },
  profileInfo: { flex: 1, minWidth: 0 },
  workerName: { fontSize: '22px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  ratingRow: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' },
  ratingText: { fontSize: '14px', color: '#999', marginLeft: '6px' },
  responseTimeBadge: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '4px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', display: 'inline-block', marginBottom: '6px' },
  reliabilityBadgeGood: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' },
  reliabilityBadgeWarn: { backgroundColor: '#fff3cd', color: '#856404', padding: '3px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' },
  location: { fontSize: '14px', color: '#999', marginTop: '6px' },
  rateBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#fff0f3', borderRadius: '10px', padding: '12px 16px', flexShrink: 0 },
  rate: { fontSize: '26px', fontWeight: 'bold', color: '#E94560' },
  perHour: { fontSize: '13px', color: '#999' },
  section: { marginBottom: '20px' },
  sectionTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  bio: { fontSize: '14px', color: '#555', lineHeight: '1.6' },
  tradeProfilesList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  tradeProfileCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px', border: '1px solid #e0e0e0' },
  tradeProfileHeader: { marginBottom: '10px' },
  tradeProfileTrade: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', display: 'block', marginBottom: '6px' },
  tradeProfileBadgeRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  levelBadge: { backgroundColor: '#1a1a2e', color: 'white', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  yearsBadge: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  verifiedLicenseBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  pendingLicenseBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  noLicenseBadge: { backgroundColor: '#f0f0f0', color: '#666', padding: '3px 10px', borderRadius: '10px', fontSize: '11px' },
  tradeProfileDetailRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '8px' },
  tradeDetailLabel: { fontSize: '11px', color: '#999', fontWeight: 'bold', textTransform: 'uppercase', minWidth: '90px', paddingTop: '4px' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  industryTag: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' },
  specTag: { backgroundColor: '#f0f0f0', color: '#444', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' },
  licenseText: { fontSize: '13px', color: '#333' },
  tagsRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tradeTag: { padding: '5px 12px', backgroundColor: '#f0f0f0', borderRadius: '12px', fontSize: '13px', color: '#444' },
  certBadge: { padding: '4px 10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  complianceRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  complianceBadge: { padding: '5px 12px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  agreementsIncompleteCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px', border: '2px solid #E94560', textAlign: 'center' },
  agreementsIncompleteTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  agreementsIncompleteText: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '20px' },
  goToProfileBtn: { padding: '12px 24px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', touchAction: 'manipulation' },
  paymentSetupCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '32px', border: '2px solid #FFB800', textAlign: 'center' },
  paymentSetupIcon: { fontSize: '48px', marginBottom: '16px' },
  paymentSetupTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  paymentSetupText: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '20px' },
  paymentSetupFeatures: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px', textAlign: 'left' },
  paymentSetupFeature: { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#333' },
  paymentSetupBtn: { padding: '14px 32px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', touchAction: 'manipulation' },
  cardEntryCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px', border: '2px solid #E94560' },
  cardEntryTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  cardEntrySubtitle: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '16px' },
  cardEntryInfo: { backgroundColor: '#e8f4fd', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px' },
  cardEntryInfoIcon: { fontSize: '16px', flexShrink: 0 },
  cardEntryInfoText: { fontSize: '13px', color: '#2c5282', lineHeight: '1.5' },
  paymentOnFileBanner: { backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#155724', fontWeight: 'bold' },
  cardInfoText: { fontWeight: 'normal', color: '#276749' },
  requestCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px' },
  requestTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '16px' },
  requestPreview: { display: 'flex', flexDirection: 'column', gap: '16px' },
  requestText: { fontSize: '14px', color: '#555', lineHeight: '1.6' },
  estimateRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  estimateLabel: { fontSize: '14px', color: '#666' },
  estimateValue: { fontSize: '18px', fontWeight: 'bold', color: '#E94560' },
  lockedDatesPreview: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  lockedDatesBox: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  lockedDatesHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  lockedDatesTitle: { fontSize: '14px', fontWeight: 'bold', color: '#276749' },
  lockedBadge: { fontSize: '12px', backgroundColor: '#276749', color: 'white', padding: '3px 8px', borderRadius: '10px' },
  lockedDatesContent: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' },
  lockedDateItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  lockedDateLabel: { fontSize: '11px', color: '#276749', fontWeight: 'bold', textTransform: 'uppercase' },
  lockedDateValue: { fontSize: '13px', color: '#333' },
  lockedNote: { fontSize: '12px', color: '#276749', marginTop: '10px', fontStyle: 'italic' },
  weekendBox: { border: '1px solid #e0e0e0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', backgroundColor: '#fafafa' },
  weekendTitle: { fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e', display: 'block', marginBottom: '10px' },
  weekendOptions: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  weekendLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#333' },
  addonBox: { border: '1px solid #e0e0e0', borderRadius: '10px', padding: '16px', marginBottom: '16px', backgroundColor: '#fafafa' },
  addonHeader: { marginBottom: '12px' },
  addonTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  addonLabel: { display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' },
  checkbox: { marginTop: '2px', accentColor: '#E94560', width: '16px', height: '16px', flexShrink: 0 },
  addonInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  addonName: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
  addonDesc: { fontSize: '12px', color: '#888', lineHeight: '1.5' },
  estimateBox: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '4px' },
  estimateTotal: { fontSize: '28px', fontWeight: 'bold', color: '#E94560' },
  estimateBreakdown: { display: 'flex', flexDirection: 'column', gap: '2px' },
  estimateNote: { fontSize: '12px', color: '#999' },
  bgCheckNote: { fontSize: '12px', color: '#E94560', fontWeight: 'bold' },
  row: { display: 'flex', gap: '12px' },
  daysNote: { fontSize: '13px', color: '#E94560', fontWeight: 'bold', marginBottom: '12px', marginTop: '-8px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px' },
  input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '15px', boxSizing: 'border-box', marginBottom: '16px' },
  textarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '15px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '16px' },
  suggestionBox: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' },
  suggestionItem: { padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: '#333', borderBottom: '1px solid #f0f0f0' },
  formButtons: { display: 'flex', gap: '12px' },
  cancelButton: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' },
  requestButton: { width: '100%', padding: '14px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  submitButton: { flex: 1, padding: '12px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation' },
  submitButtonDisabled: { flex: 1, padding: '12px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'not-allowed' },
  successCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center' },
  successIcon: { fontSize: '48px', marginBottom: '16px' },
  successTitle: { fontSize: '24px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  successText: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '24px' },
};

export default WorkerDetail;